// src/pages/TermsOfService.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './LegalPages.css';

function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Home
        </button>

        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: January 12, 2026</p>

        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing and using TaalumaERP ("Service"), you agree to be bound by these Terms of Service 
            ("Terms"). If you do not agree to these Terms, do not use the Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Description of Service</h2>
          <p>
            TaalumaERP provides cloud-based inventory management software with offline capabilities. 
            The Service includes:
          </p>
          <ul>
            <li>Inventory tracking across multiple warehouses</li>
            <li>Order management</li>
            <li>Offline functionality for up to 7 days</li>
            <li>Multi-currency support</li>
            <li>Mobile money payment integration</li>
            <li>Analytics and reporting</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Account Registration</h2>
          <p>
            To use the Service, you must:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information during registration</li>
            <li>Maintain and update your account information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized access</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Subscription Plans and Pricing</h2>
          <p>
            <strong>4.1 Free Trial:</strong> We offer a 14-day free trial with full access to features. 
            No credit card required. The trial automatically ends after 14 days.
          </p>
          <p>
            <strong>4.2 Paid Subscriptions:</strong> After the trial, you may choose:
          </p>
          <ul>
            <li>Monthly subscription (billed monthly)</li>
            <li>Perpetual license (one-time payment, lifetime access)</li>
          </ul>
          <p>
            <strong>4.3 Payment:</strong> All fees are in the currency selected at purchase (USD, EUR, TZS, or UGX). 
            Payment is processed via Stripe or mobile money services.
          </p>
          <p>
            <strong>4.4 Price Changes:</strong> We reserve the right to change pricing with 30 days notice. 
            Changes do not affect active subscriptions until renewal.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Refund Policy</h2>
          <p>
            <strong>5.1 Monthly Subscriptions:</strong> No refunds for monthly subscriptions. You may cancel 
            at any time, and service continues until the end of the billing period.
          </p>
          <p>
            <strong>5.2 Perpetual Licenses:</strong> Refunds available within 30 days of purchase if you 
            haven't used the Service extensively (more than 100 orders created).
          </p>
        </section>

        <section className="legal-section">
          <h2>6. User Responsibilities</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Violate any laws or regulations</li>
            <li>Share your account with unauthorized users</li>
            <li>Attempt to reverse engineer or hack the Service</li>
            <li>Use the Service to store illegal content</li>
            <li>Resell or redistribute the Service</li>
            <li>Send spam or malicious content</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Data and Privacy</h2>
          <p>
            Your use of the Service is also governed by our Privacy Policy. We store your data securely 
            and do not sell your information to third parties.
          </p>
          <p>
            <strong>Data Backup:</strong> While we perform regular backups, you are responsible for 
            maintaining your own backups of critical data.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Offline Functionality</h2>
          <p>
            The Service allows offline use for up to 7 days. After 7 days offline, you must reconnect 
            to the internet to sync data. We are not responsible for data loss due to extended offline use.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Service Availability</h2>
          <p>
            We strive for 99.9% uptime but do not guarantee uninterrupted service. We are not liable for:
          </p>
          <ul>
            <li>Scheduled maintenance (announced in advance)</li>
            <li>Internet service provider issues</li>
            <li>Force majeure events</li>
            <li>Third-party service failures</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>10. Termination</h2>
          <p>
            <strong>10.1 By You:</strong> You may cancel your subscription at any time from your account settings.
          </p>
          <p>
            <strong>10.2 By Us:</strong> We may suspend or terminate your account if you:
          </p>
          <ul>
            <li>Violate these Terms</li>
            <li>Fail to pay fees</li>
            <li>Engage in fraudulent activity</li>
            <li>Abuse the Service</li>
          </ul>
          <p>
            <strong>10.3 Effect:</strong> Upon termination, you lose access to the Service. Your data 
            will be retained for 30 days for recovery, then permanently deleted.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, TaalumaERP is not liable for:
          </p>
          <ul>
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of profits, revenue, or data</li>
            <li>Business interruption</li>
            <li>Damages exceeding the amount paid in the last 12 months</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Warranty Disclaimer</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. 
            We do not warrant that the Service will be error-free or uninterrupted.
          </p>
        </section>

        <section className="legal-section">
          <h2>13. Indemnification</h2>
          <p>
            You agree to indemnify and hold TaalumaERP harmless from any claims, damages, or expenses 
            arising from your use of the Service or violation of these Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of Italy. Any disputes shall be resolved in the 
            courts of Modena, Italy.
          </p>
        </section>

        <section className="legal-section">
          <h2>15. Changes to Terms</h2>
          <p>
            We may modify these Terms at any time. We will notify you of material changes via email 
            or in-app notification. Continued use after changes constitutes acceptance.
          </p>
        </section>

        <section className="legal-section">
          <h2>16. Contact Information</h2>
          <p>
            For questions about these Terms, contact us at:
          </p>
          <p>
            <strong>Email:</strong> <a href="mailto:support@taalumaerp.com">support@taalumaerp.com</a><br />
            <strong>Address:</strong> Modena, Italy
          </p>
        </section>
      </div>
    </div>
  );
}

export default TermsOfService;
